/**
 * The chosen cashflow & Age Pension plan, as the plan page and Home read it (cashflow rebuild, 21 Oct 2026 —
 * the village's rvChosenPlan.ts and Support at home's sahChosenPlan.ts).
 *
 * The card the client chose is stored on the record: its three figures, changing nothing's at the time, the
 * plan in words, its parts and pieces, the support cost and the years it was priced on. So the plan page shows
 * exactly what was chosen, with no re-run in between, and a later change of support cost can be said against
 * it ("priced on $12,000").
 */
import type { RiskKind } from '../../strategy/strategyRisks.ts';
import type { PlanPiece } from '../../components/narrative/PlanPieces.tsx';

export interface CpFigures { legacy: number; pension: number; cashflow: number }

export interface CpPlanCardRecord {
    plan: CpFigures;
    baseline: CpFigures | null;
    words: { title: string; sub: string };
    horizonYears: number;
    riskKinds: RiskKind[];
    pieces: PlanPiece[];
    /** The reasons it does what it does, in plain words, as Explain this said them when chosen. */
    why: string[];
    support: { annual: number; label: string };
    /** The guaranteed income it buys, if any — the next step is a quote. */
    annuity: { amount: number; type: string; residualPercent: number } | null;
    /** 'engine' — one of the tested plans; 'manual' — built by hand. */
    origin: 'engine' | 'manual';
    /**
     * The plan's rows and changing nothing's, cut to what Explain this reads (compactStoryRows), so the chosen plan's
     * Explain this has the same lines and money story as the tested plan's (23 Oct 2026). Absent on older cards.
     */
    rows?: Array<Record<string, unknown>> | null;
    baseRows?: Array<Record<string, unknown>> | null;
    /** What the family receives from a guaranteed income, frozen when chosen (AnnuityFamilyPanel's rows). */
    family?: {
        personName: string;
        amount: number;
        kind: 'fullBack' | 'moneyBack' | 'incomeOnly';
        incomeAYear: number | null;
        compareNote: string | null;
        rows: Array<{ label: string; fromAnnuity: number; withPlan: number | null; without: number | null }>;
    } | null;
}

/** Session key: Home's "Start a new model" — open on an empty step 1 and ask step 3's questions again (one-shot). */
export const CP_START_FRESH_KEY = 'careCompanionCpStartFresh';

/** The chosen cashflow plan on the file that carries its card, if one is chosen. */
export function chosenCpPlanOf(list: ReadonlyArray<Record<string, unknown>> | null | undefined): Record<string, unknown> | null {
    return (list || []).find((s) => s?.type === 'cashflow_pension' && s.chosen === true) ?? null;
}

/** The card of a saved record, or null for a plan saved before the rebuild. */
export function cpCardOf(record: unknown): CpPlanCardRecord | null {
    const card = (record as { inputs?: { card?: unknown } } | null | undefined)?.inputs?.card;
    return card && typeof card === 'object' && (card as { plan?: unknown }).plan ? (card as CpPlanCardRecord) : null;
}

/** Reads a one-shot session flag and clears it. */
export function takeCpSessionFlag(key: string): boolean {
    try {
        const v = window.sessionStorage.getItem(key);
        if (v) window.sessionStorage.removeItem(key);
        return !!v;
    } catch {
        return false;
    }
}

/** "Priced on $12,000 of support a year" — whether the support cost on screen differs from the chosen plan's. */
export function supportChangedSince(card: CpPlanCardRecord | null | undefined, currentAnnual: number): boolean {
    if (!card) return false;
    return Math.round(Number(card.support?.annual) || 0) !== Math.round(Number(currentAnnual) || 0);
}
